import React, { useEffect } from "react";
import Helmet from "react-helmet";
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams
} from "react-router-dom";
import { ThemeProvider, useTheme } from "styled-components";
import Analytics from "../common/Analytics";
import FauxLoader from "../common/FauxLoader";
import Telestrator from "../common/Telestrator";
import LiveFooter from "./common/LiveFooter";
import LiveNotFound from "./common/LiveNotFound";
import LiveScrollRestoration from "./common/LiveScrollRestoration";
import LiveMap from "./comps/LiveMap";
import LiveStats from "./comps/LiveStats";
import { LiveProvider, useLive } from "./LiveProvider";
import LiveSplash from "./LiveSplash";
import {
  Container,
  Content,
  GlobalStyle,
  LoadingContainer
} from "./styled/live";

function Live() {
  const theme = useTheme();
  const { guid } = useParams();
  const { pathname } = useLocation();
  const {
    agent,
    appLoaded,
    appError,
    theme: currentTheme,
    isDrawing,
    setIsDrawing,
    modules,
    clearGhostListings,
    setAnimationLoaded
  } = useLive();
  const primaryColor = `#${currentTheme.colors.primary}`;
  const secondaryColor = `#${currentTheme.colors.secondary}`;
  const liveTheme = {
    live: {
      colors: {
        primary: primaryColor,
        secondary: secondaryColor
      }
    }
  };
  const subModules = modules.reduce(
    (subModules, module) => [...subModules, ...module.subModules],
    []
  );

  useEffect(() => {
    setIsDrawing(false);
    clearGhostListings();
  }, [pathname]);

  // Keyboard shortcut for drawing
  useEffect(() => {
    const handler = (e) => {
      const inputElements = ["input", "textarea"];
      if (inputElements.includes(e.target.nodeName.toLowerCase())) {
        return;
      }
      if (e.key === "p" || e.key === "P") {
        setIsDrawing((isDrawing) => !isDrawing);
      } else if (e.key === "Escape") {
        setIsDrawing(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
    };
  }, []);

  return (
    <ThemeProvider theme={liveTheme}>
      <GlobalStyle />
      <Helmet>
        <title>Cloud CMA | Live Report</title>
      </Helmet>
      {!appLoaded && (
        <LoadingContainer className="loading-container">
          <FauxLoader
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            agentCompanyName={agent.companyName}
            agentCompanyLogo={agent.companyLogo}
            agentWebsite={agent.website}
            error={appError}
            onComplete={() => setAnimationLoaded(true)}
          />
        </LoadingContainer>
      )}
      {appLoaded && (
        <>
          <Analytics />
          <LiveScrollRestoration />
          <Container className="container">
            <Content>
              <Switch>
                {/* Special routes that don't need to be in modules */}
                <Route path="/live/:guid" exact>
                  <LiveSplash />
                </Route>
                <Route path="/live/:guid/comps/map" exact>
                  <LiveMap />
                </Route>
                <Route path="/live/:guid/comps/list">
                  <Redirect to={`/live/${guid}/comps/stats`} />
                </Route>
                <Route path="/live/:guid/comps/stats" exact>
                  <LiveStats />
                </Route>
                {modules.map((module) => (
                  <Route key={module.key} path={module.path} exact>
                    {module.el}
                  </Route>
                ))}
                {subModules.map((subModule) => (
                  <Route key={subModule.key} path={subModule.path} exact>
                    {subModule.el}
                  </Route>
                ))}
                <Route>
                  <LiveNotFound />
                </Route>
              </Switch>
              {isDrawing && (
                <Telestrator
                  color={theme.styleGuide.colors.helix['light-green']}
                  strokeSize={6}
                />
              )}
            </Content>
            <LiveFooter />
          </Container>
        </>
      )}
    </ThemeProvider>
  );
}

export default () => (
  <LiveProvider>
    <Live />
  </LiveProvider>
);
